/**
 * Utility functions for managing marketing parameters across the app
 * Marketing params are preserved in session storage and automatically
 * appended to all navigation
 */

const MARKETING_PARAMS_KEY = 'clickguard_marketing_params'

/**
 * Get marketing params from session storage
 */
export function getMarketingParams(): Record<string, string> {
	try {
		const stored = sessionStorage.getItem(MARKETING_PARAMS_KEY)
		return stored ? JSON.parse(stored) : {}
	} catch (e) {
		console.error('Error reading marketing params from session storage:', e)
		return {}
	}
}

/**
 * Store marketing params in session storage
 */
export function setMarketingParams(params: Record<string, string>): void {
	try {
		if (Object.keys(params).length > 0) {
			sessionStorage.setItem(MARKETING_PARAMS_KEY, JSON.stringify(params))
		}
	} catch (e) {
		console.error('Error storing marketing params in session storage:', e)
	}
}

/**
 * Clear marketing params from session storage
 */
export function clearMarketingParams(): void {
	try {
		sessionStorage.removeItem(MARKETING_PARAMS_KEY)
	} catch (e) {
		console.error('Error clearing marketing params from session storage:', e)
	}
}

/**
 * Append marketing params to a URL
 * @param url - The base URL (can be relative path or full URL)
 * @param additionalParams - Optional additional params to include
 * @returns URL with marketing params appended
 */
export function appendMarketingParams(
	url: string,
	additionalParams?: Record<string, string>
): string {
	const marketingParams = getMarketingParams()
	const allParams = { ...marketingParams, ...additionalParams }

	if (Object.keys(allParams).length === 0) {
		return url
	}

	const separator = url.includes('?') ? '&' : '?'
	const paramString = Object.keys(allParams)
		.map(
			(key) =>
				`${encodeURIComponent(key)}=${encodeURIComponent(allParams[key])}`
		)
		.join('&')

	return `${url}${separator}${paramString}`
}

/**
 * Build query string from marketing params
 * @returns Query string (without leading ?)
 */
export function buildMarketingParamsQueryString(): string {
	const params = getMarketingParams()
	if (Object.keys(params).length === 0) {
		return ''
	}

	return Object.keys(params)
		.map(
			(key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
		)
		.join('&')
}

/**
 * Get marketing params as URLSearchParams object
 */
export function getMarketingParamsAsURLSearchParams(): URLSearchParams {
	const params = getMarketingParams()
	const searchParams = new URLSearchParams()
	Object.keys(params).forEach((key) => {
		searchParams.set(key, params[key])
	})
	return searchParams
}

